import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DELETE_ALL_VARIABLES_ID,
  GLOBAL_WORKFLOW_ID,
  VariableApi,
  VariableCreateRequest,
  VariableDeleteRequest,
  VariableGroupApi,
  VariableImportItem,
  VariableImportResponse,
  VariableUpdateRequest,
} from '@selfai-platform/pipeline-common';
import { PipelineConfigService } from '@selfai-platform/shared';
import { Observable } from 'rxjs';

const API_ENDPOINTS = {
  VARIABLES_BASE: 'variables',
  CREATE: 'create',
  IMPORT: 'import',
  UPDATE: 'update',
  DELETE_WORKFLOW: 'delete/workflow',
};

const QUERY_PARAMS = {
  OVERWRITE: 'overwrite',
  INCLUDE_GLOBAL: 'includeGlobal',
};

@Injectable({
  providedIn: 'root',
})
export class VariablesApiService {
  private readonly apiUrl: string;

  constructor(
    private readonly http: HttpClient,
    private readonly pipelineConfigService: PipelineConfigService,
  ) {
    const config = this.pipelineConfigService.getConfig();
    this.apiUrl = `${config.hosts.api}/${config.versions.url}`;
  }

  getVariable(variableId: string): Observable<VariableApi | VariableGroupApi> {
    const url = this.buildUrl('', variableId);
    return this.http.get<VariableApi | VariableGroupApi>(url);
  }

  getWorkflowVariables(
    workflowId: string,
    includeGlobal: boolean = false,
  ): Observable<VariableApi[] | VariableGroupApi[]> {
    const url = this.buildUrl('workflow', workflowId);
    const params = new HttpParams().set(QUERY_PARAMS.INCLUDE_GLOBAL, includeGlobal.toString());

    return this.http.get<VariableApi[] | VariableGroupApi[]>(url, { params });
  }

  getGlobalVariables(): Observable<VariableApi[] | VariableGroupApi[]> {
    const url = this.buildUrl();
    return this.http.get<VariableApi[] | VariableGroupApi[]>(url);
  }

  createVariable(request: VariableCreateRequest): Observable<VariableApi | VariableGroupApi> {
    const url = this.buildUrl(API_ENDPOINTS.CREATE);
    return this.http.post<VariableApi | VariableGroupApi>(url, request);
  }

  importVariables(
    workflowId: string,
    variables: VariableImportItem[],
    overwrite: boolean = false,
  ): Observable<VariableImportResponse[]> {
    const url = this.buildUrl(API_ENDPOINTS.IMPORT, workflowId);
    const params = new HttpParams().set(QUERY_PARAMS.OVERWRITE, overwrite.toString());

    return this.http.post<VariableImportResponse[]>(url, variables, { params });
  }

  importGlobalVariables(
    variables: VariableImportItem[],
    overwrite: boolean = false,
  ): Observable<VariableImportResponse[]> {
    return this.importVariables(GLOBAL_WORKFLOW_ID, variables, overwrite);
  }

  updateVariable(variableId: string, request: VariableUpdateRequest): Observable<VariableApi | VariableGroupApi> {
    const url = this.buildUrl(API_ENDPOINTS.UPDATE, variableId);
    return this.http.post<VariableApi | VariableGroupApi>(url, request);
  }

  deleteWorkflowVariables(workflowId: string, request: VariableDeleteRequest): Observable<string[]> {
    const url = this.buildUrl(API_ENDPOINTS.DELETE_WORKFLOW, workflowId);
    return this.http.delete<string[]>(url, { body: request });
  }

  deleteGlobalVariables(request: VariableDeleteRequest): Observable<string[]> {
    return this.deleteWorkflowVariables(GLOBAL_WORKFLOW_ID, request);
  }

  deleteAllWorkflowVariables(workflowId: string): Observable<string[]> {
    const request: VariableDeleteRequest = {
      variables: [DELETE_ALL_VARIABLES_ID],
    };
    return this.deleteWorkflowVariables(workflowId, request);
  }

  deleteAllGlobalVariables(): Observable<string[]> {
    const request: VariableDeleteRequest = {
      variables: [DELETE_ALL_VARIABLES_ID],
    };
    return this.deleteGlobalVariables(request);
  }

  private buildUrl(...pathSegments: string[]): string {
    const segments = [this.apiUrl, API_ENDPOINTS.VARIABLES_BASE, ...pathSegments.filter(Boolean)];
    return segments.join('/');
  }
}
